import { isUndefined } from 'lodash'
import { defineStore } from 'pinia'

import { useNotificationProvider } from '@shared/composables/use-notification-provider'
import { subjectsForShowNotification } from '@shared/constants/notifications'
import { Namespaces } from '@shared/models/enums/namespaces'
import { SocketChannels } from '@shared/models/enums/websocket-channels'
import { NotificationChannelEvents } from '@shared/models/enums/websocket-events'
import { StoreState } from '@shared/models/interfaces/store'
import { WebNotificationNotificationData } from '@shared/models/proto/base/decorators/web-notification-data'
import { INotification } from '@shared/models/proto/base/notifications'
import {
  GetNotificationsDTO,
  SetNotificationAsReadedDTO,
} from '@shared/models/proto/dto/notifications'
import { GetNotificationsRO } from '@shared/models/proto/ro/notifications'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { SessionProvider } from '@shared/plugins/session-provider'
import { PhoenixSocket } from '@shared/plugins/socket'
import { useAppsHeaderStore } from '@shared/store/apps-header'
import { INITIAL_PAGINATION } from '@shared/utils/constants/pagination'
import {
  getInitialState,
  updateState,
} from '@shared/utils/store/http-request-state'

interface State {
  notifications: StoreState<GetNotificationsRO>
}

const state: () => State = () => ({
  notifications: getInitialState(),
})

export const useNotificationsStore = (namespace: Namespaces) =>
  defineStore({
    id: 'notifications',

    state,

    actions: {
      getApiTransport() {
        return new ApiTransport(resourcePath, namespace as string)
      },

      createNotificationChannel() {
        PhoenixSocket.findChannel(
          SessionProvider.chatUserId(),
          SocketChannels.NotificationChannel
        ).then(({ channel }) => {
          channel.on(NotificationChannelEvents.Notification, (json) => {
            this.addNewNotification(json)
          })
        })
      },

      closeNotificationChannel() {
        PhoenixSocket.leaveChannel(
          SessionProvider.chatUserId(),
          SocketChannels.NotificationChannel
        )
      },

      async fetchNotifications(
        payload: { page: number; perPage: number },
        isFetched = false
      ) {
        if (isFetched) {
          return
        }

        try {
          const apiTransport = this.getApiTransport()

          const { data, meta } = (await apiTransport.get<
            GetNotificationsDTO,
            GetNotificationsRO
          >(
            '/web_notifications',
            new GetNotificationsDTO(payload),
            GetNotificationsRO
          )) ?? { data: [], meta: {} }

          const { data: currentNotificationsList = [] } =
            this.notifications.data ?? {}

          // Remove any notifications that might already be in the list from the websocket
          const filteredCurrentNotifications = currentNotificationsList.filter(
            ({ id }) => !data.some((d) => d.id === id)
          )

          const updatedNotificationsList = [
            ...filteredCurrentNotifications,
            ...data,
          ]

          this.notifications = updateState(
            this.notifications,
            new GetNotificationsRO({
              data: updatedNotificationsList,
              meta,
            })
          )
        } catch (e) {
          this.notifications = updateState(
            this.notifications,
            new Error(`${e}`)
          )
          return Promise.reject(e)
        }
      },

      async setNotificationAsReaded(notificationId: number) {
        const headerStore = useAppsHeaderStore()
        const apiTransport = this.getApiTransport()

        await apiTransport.post<SetNotificationAsReadedDTO>(
          '/web_notifications/clear',
          new SetNotificationAsReadedDTO({
            data: {
              ids: [notificationId],
            },
          }),
          SetNotificationAsReadedDTO
        )

        const { data } = this.notifications.data ?? {}
        const isNotificationExists = data?.some((n) => n.id === notificationId)

        if (!isNotificationExists) {
          return
        }

        const filteredNotifications = data?.filter(
          (notification) => notification.id !== notificationId
        )

        if (!isUndefined(headerStore.counts.data?.webNotificationsCount)) {
          headerStore.counts.data!.webNotificationsCount--
        }

        this.notifications = updateState(
          this.notifications,
          new GetNotificationsRO({
            data: filteredNotifications ?? [],
            meta: {
              ...this.meta,
              totalCount: this.meta.totalCount - 1,
            },
          })
        )
      },

      addNewNotification(notification: INotification) {
        const { checkNotificationAndShow } = useNotificationProvider()
        const headerStore = useAppsHeaderStore()

        const isCsvProcessed = subjectsForShowNotification.includes(
          notification.subject
        )

        if (isCsvProcessed) {
          checkNotificationAndShow(notification)
          return
        }

        if (!isUndefined(headerStore.counts.data?.webNotificationsCount)) {
          headerStore.counts.data!.webNotificationsCount++
        }

        const { data } = this.notifications.data ?? { data: [], meta: {} }

        this.notifications = updateState(
          this.notifications,
          new GetNotificationsRO({
            data: [notification, ...(data ?? [])],
            meta: {
              ...this.meta,
              totalCount: this.meta.totalCount + 1,
            },
          })
        )
      },
    },
    getters: {
      meta: (state) => state.notifications?.data?.meta ?? INITIAL_PAGINATION,
      list: (state) =>
        state.notifications?.data?.data?.map(
          (item: INotification) => new WebNotificationNotificationData(item)
        ),
    },
  })()
